// @ts-strict-ignore

import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import { filter, first, take } from 'rxjs/operators';

import { CategoryViewType } from 'src/app/api/owcc/models';
import { AppDomContextService } from 'src/app/app-dom-context.service';
import { appInject } from 'src/app/app-injector';
import { LinkService } from 'src/app/core/services/link/link.service';
import { ScrollingService } from 'src/app/core/services/scrolling/scrolling.service';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingLabel } from 'src/app/core/services/tracking/models/tracking-label.enum';
import { TrackingInput } from 'src/app/core/services/tracking/models/tracking-object.interface';
import { startInitializeAction } from 'src/app/features/configuration/store/configuration.actions';
import { AppRoutePathParam } from 'src/app/routing/app-route-path-param.enum';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import {
  fromNavigation,
  selectHasComponents,
  selectHasVehicleLengths,
  selectVehicleIdForUpcomingConfiguration
} from 'src/app/store/common/common.selectors';
import { noop } from 'src/app/utils/functions/noop';
import { observeOnResize } from 'src/app/utils/functions/observe-on-resize';
import { filterChanged, filterFalsy, filterTruthy } from 'src/app/utils/functions/rx-filters';
import { CONTENT_SCROLL_ANCHOR_ID } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { TranslatePipe } from '../pipes/translate/translate.pipe';

import { NavigationGroup } from './navigation-group.interface';
import { NavigationItem } from './navigation-item.interface';

export const createNavigationTrackingData = (label: TrackingLabel): TrackingInput => ({
  clickData: {
    label,
    category: TrackingCategory.Link,
    action: TrackingAction.Navigation
  }
});

interface NavigationComponentFlatItem {
  group: string;
  child: NavigationItem;
  isTranslated?: boolean;
}

@Component({
  selector: 'cc-navigation',
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, TranslatePipe, NgTemplateOutlet, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NavigationComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() public readonly isOpenValueChange = new EventEmitter<boolean>();
  @ViewChild('navigationContainer', { read: ElementRef }) private readonly container: ElementRef<HTMLElement>;
  @ViewChild('mobileToggle', { read: ElementRef }) private readonly mobileToggle: ElementRef<HTMLElement>;

  public items: NavigationGroup[] = [];
  public isOpen = false;
  public currentIndex = -1;
  public currentItem: NavigationItem | null = null;
  public currentGroup: NavigationGroup | null = null;
  public flatItems: NavigationComponentFlatItem[];
  public isReadyToDisplay = false;

  public readonly categoryViewType = CategoryViewType;

  public selectShowVehicleLengthPage = this.store.select(selectHasVehicleLengths);

  public readonly trackingData = {
    submenu: createNavigationTrackingData(TrackingLabel.NaviBarSubMenu).clickData,
    menu: createNavigationTrackingData(TrackingLabel.NaviBar).clickData,
    arrowPrevious: createNavigationTrackingData(TrackingLabel.LeftArrow).clickData,
    arrowNext: createNavigationTrackingData(TrackingLabel.RightArrow).clickData
  };

  private currentWindowHeight = -1;
  private vehicleId: string;

  public constructor(
    private readonly linkService: LinkService,
    private readonly scrollingService: ScrollingService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(WINDOW) private readonly window: Window,
    private readonly appDomContext: AppDomContextService,
    private readonly store: Store
  ) {}
  public ngOnInit(): void {
    this.isOpenValueChange.emit(false);
    this.subscribeToConfigOrVehicleChange();
    this.subscribeToCurrentNavState();
    this.subscribeToWindowResize();
    this.preloadSelectables();
  }

  public ngAfterViewInit(): void {
    observeOnResize(this.container.nativeElement)
      .pipe(
        filter(entry => entry.length > 0 && entry[0].target?.clientHeight > 0),
        first()
      )
      .subscribe(() => {
        this.isReadyToDisplay = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this.toggleDropdown(false);
  }

  public navigateToIndex(index: number): void {
    if (this.currentIndex !== index && this.flatItems[index]) {
      this.navigateToItem(this.flatItems[index].child);
    }
  }

  public onNavigate(navigationItem: NavigationItem, routerLink: string): void {
    const isActive = navigationItem.id === this.currentItem.id;

    // To not have the dropdown in the user's field of view after navigating to the content area
    this.toggleDropdown(false);
    this.router
      .navigate([routerLink], {
        ...this.route.snapshot.queryParams,
        queryParamsHandling: 'merge',
        fragment: isActive ? CONTENT_SCROLL_ANCHOR_ID : undefined
      })
      .catch(noop);
  }

  public getRouterLink$(navigationItem: NavigationItem): Observable<string> {
    if (navigationItem.id.startsWith('SUMMARY')) {
      return this.linkService.getConfigurationSummaryLink(this.vehicleId);
    }
    return this.linkService.getLink(navigationItem.routePath, {
      ...navigationItem.pathParams,
      [AppRoutePathParam.VehicleId]: this.vehicleId
    });
  }

  public toggleDropdown(shouldShow: boolean): void {
    this.isOpen = shouldShow;
    this.isOpenValueChange.emit(shouldShow);
    if (shouldShow) this.scrollingService.disableScrolling();
    else this.scrollingService.enableScrolling();
    this.toggleContainerHeight(shouldShow);
  }

  public toggleDropdownAndChangeFocus(): void {
    if (!this.mobileToggle) {
      return;
    }
    this.mobileToggle.nativeElement?.focus();
    this.toggleDropdown(false);
  }

  // if there are no selectables yet available in the store, load selectables for current configuration (e.g. on Motorization)
  private preloadSelectables(): void {
    this.store
      .select(selectHasComponents)
      .pipe(take(1), filterFalsy(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(() => {
        this.store.dispatch(startInitializeAction());
      });
  }

  private subscribeToConfigOrVehicleChange(): void {
    combineLatest([
      this.store.select(fromNavigation.selectNavigationGroups).pipe(filterTruthy()),
      this.store.select(selectVehicleIdForUpcomingConfiguration).pipe(filterTruthy())
    ])
      .pipe(filterChanged(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(([groups, vehicleId]) => {
        const flatItems: NavigationComponentFlatItem[] = [];
        if (Array.isArray(groups)) {
          for (const group of groups) {
            for (const child of group.children) {
              flatItems.push({
                group: group.name,
                child
              });
            }
          }
        }
        this.vehicleId = vehicleId;
        this.toggleDropdown(false);
        this.items = groups;
        this.flatItems = flatItems;
        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToCurrentNavState(): void {
    this.store
      .select(fromNavigation.selectCurrentNavigationElement)
      .pipe(filterTruthy(), filterChanged(), takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(({ index, item, group }) => {
        this.currentGroup = group;
        this.currentItem = item;
        this.currentIndex = index;
        this.toggleDropdown(false);
        this.changeDetectorRef.markForCheck();
      });
  }

  private subscribeToWindowResize(): void {
    fromEvent(this.window, 'resize')
      .pipe(takeUntilDestroyed(appInject(DestroyRef)))
      .subscribe(() => {
        if (this.isOpen && this.currentWindowHeight !== this.window.innerHeight) {
          // vertical resize
          this.scrollingService.disableScrolling();
          this.toggleContainerHeight(true);
          this.currentWindowHeight = this.window.innerHeight;
        } else {
          // horizontal resize
          this.toggleDropdown(false);
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  private navigateToItem(navigationItem: NavigationItem): void {
    const pathToLinkMapping: Partial<Record<AppRoutePath | 'default', Observable<string>>> = {
      [AppRoutePath.Preconfig]: this.linkService.getPreconfigLink(),
      [AppRoutePath.VehicleLength]: this.linkService.getVehicleLengthLink(),
      [AppRoutePath.Motorization]: this.linkService.getMotorizationLink(),
      [AppRoutePath.Configuration]: this.linkService.getConfigurationLink(
        this.vehicleId,
        (navigationItem.pathParams?.configurationStep || '') as string
      ),
      default: this.linkService.getLink(navigationItem.routePath, {
        ...navigationItem.pathParams,
        [AppRoutePathParam.VehicleId]: this.vehicleId
      })
    };

    pathToLinkMapping[navigationItem.routePath].pipe(first()).subscribe(path => {
      this.handleRoutePath(path);
    });
  }

  private handleRoutePath(path: string): void {
    this.toggleDropdown(false);
    this.router.navigate([path], { preserveFragment: true }).catch(noop);
  }

  private toggleContainerHeight(shouldShow: boolean): void {
    if (!this.container?.nativeElement) {
      return;
    }

    const container = this.container.nativeElement;

    let yPos: number;
    let parentElement: HTMLElement;
    let oneWebNexStepNavigation: HTMLElement;
    let height = '';
    let position = '';

    if (shouldShow) {
      let differenceToFullHeight = this.window.scrollY;

      oneWebNexStepNavigation = this.appDomContext.shadowRoot.querySelector('.nextstepnavigation__base');

      yPos = container.offsetTop;
      parentElement = container.offsetParent as HTMLElement;
      while (parentElement != null) {
        yPos += parentElement.offsetTop;
        parentElement = parentElement.offsetParent as HTMLElement;
      }

      differenceToFullHeight -= yPos;

      if (oneWebNexStepNavigation) {
        differenceToFullHeight -= oneWebNexStepNavigation.clientHeight;
      }

      height = `calc(100vh - ${Math.abs(differenceToFullHeight)}px)`;
      position = 'absolute';
    }

    container.style.height = height;
    container.style.position = position;
  }
}
